import { ArrowDropDown, Done } from "@mui/icons-material";
import { IconButton, Menu } from "@mui/material";
import {
  useEffect,
  useMemo,
  useState,
  type FC,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { CheckboxControlledMui, FramerTooltip } from "@ryde/controls";
import { useBulkGridSelection } from "@ryde/redux";
import { BulkGridSelectionModeEnum, TOOLTIP_DELAY_BUTTONS } from "@ryde/shared";

import { RidesSelectAllMobileIcon } from "../rides-select-all-mobile-icon";
import { BulkGridSelectionModeIndicator } from "./bulk-grid-selection-mode-indicator";
import { BulkGridSelectionModeMenuItem } from "./bulk-grid-selection-mode-menu-item";
import {
  BulkGridSelectionMenuTriggerStyled,
  BulkGridSelectionRowCountGroupStyled,
  BulkGridSelectionRowCountStyled,
  BulkGridSelectionWrapperStyled,
} from "./bulk-grid-selection.styled";
import { getBulkGridSelectionModeLabelKey } from "./bulk-grid-selection.utils";
import { useBulkGridSelectionRowCountInput } from "./use-bulk-grid-selection-row-count-input";

interface IBulkGridSelectionControlProps {
  className?: string;
  enableSelectRows?: boolean;
  enableSelectionMenu?: boolean;
}

export const BulkGridSelectionControl: FC<IBulkGridSelectionControlProps> = ({
  className,
  enableSelectRows = true,
  enableSelectionMenu = true,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [optimisticChecked, setOptimisticChecked] = useState<boolean | null>(
    null,
  );

  const {
    activeMode,
    rowCount,
    isCheckboxChecked,
    showRowCountInput,
    setRowCount,
    applyMode,
    toggleCheckbox,
  } = useBulkGridSelection({
    isSelectRowsEnabled: enableSelectRows,
    isSelectionMenuEnabled: enableSelectionMenu,
  });

  const isCheckboxDisplayedChecked = optimisticChecked ?? isCheckboxChecked;

  useEffect(() => {
    setOptimisticChecked(null);
  }, [isCheckboxChecked]);

  const openMenu = useEvent((event: MouseEvent<HTMLElement>): void => {
    setMenuAnchor(event.currentTarget);
  });

  const closeMenu = useEvent((): void => {
    setMenuAnchor(null);
  });

  const onCheckboxClick = useEvent((): void => {
    void Promise.resolve(toggleCheckbox()).then(didApply => {
      setOptimisticChecked(didApply ? !isCheckboxDisplayedChecked : null);
    });
  });

  const onMenuModeSelect = useEvent((mode: BulkGridSelectionModeEnum): void => {
    closeMenu();
    void Promise.resolve(applyMode(mode));
    setOptimisticChecked(null);
  });

  const rowCountInput = useBulkGridSelectionRowCountInput({
    rowCount,
    setRowCount: value => {
      setRowCount(value);
      setOptimisticChecked(null);
    },
  });

  const selectAllLabel = t("general.SELECT_ALL");

  const modeTooltip = useMemo((): string => {
    return t(getBulkGridSelectionModeLabelKey(activeMode));
  }, [activeMode, t]);

  const canApplyRowCount = rowCountInput.hasPendingChanges;

  const rowCountApplyLabel = canApplyRowCount
    ? t("general.SELECT_ROWS_COUNT_APPLY")
    : t("general.SELECT_ROWS_COUNT_ALREADY_APPLIED");

  const onRowCountKeyDown = useEvent(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key !== "Enter") {
        return;
      }

      event.preventDefault();

      if (rowCountInput.hasPendingChanges) {
        rowCountInput.onApply();
        setOptimisticChecked(null);
      }
    },
  );

  const onRowCountApplyMouseDown = useEvent(
    (event: MouseEvent<HTMLButtonElement>): void => {
      // Keep focus on the input until apply runs; otherwise blur would discard the draft.
      event.preventDefault();
    },
  );

  const onRowCountApplyClick = useEvent((): void => {
    if (!rowCountInput.hasPendingChanges) {
      return;
    }

    rowCountInput.onApply();
    setOptimisticChecked(null);
  });

  return (
    <BulkGridSelectionWrapperStyled className={className}>
      <CheckboxControlledMui
        width={16}
        height={16}
        checked={isCheckboxDisplayedChecked}
        setCheckedToggle={onCheckboxClick}
        e2eData="bulk-grid-selection-checkbox"
      />

      {enableSelectionMenu ? (
        <>
          <BulkGridSelectionMenuTriggerStyled>
            <BulkGridSelectionModeIndicator
              mode={activeMode}
              tooltip={modeTooltip}
            />
            <IconButton
              size="small"
              className="bulk-grid-selection__menu-arrow"
              aria-label={t("general.BULK_SELECTION_OPTIONS")}
              onClick={openMenu}
              data-e2e="bulk-grid-selection-menu-trigger"
            >
              <ArrowDropDown fontSize="small" />
            </IconButton>
          </BulkGridSelectionMenuTriggerStyled>

          {showRowCountInput && enableSelectRows && (
            <BulkGridSelectionRowCountGroupStyled>
              <BulkGridSelectionRowCountStyled
                type="text"
                size="small"
                inputMode="numeric"
                autoComplete="off"
                value={rowCountInput.inputValue}
                onFocus={rowCountInput.onFocus}
                onChange={rowCountInput.onChange}
                onBlur={rowCountInput.onBlur}
                onKeyDown={onRowCountKeyDown}
                slotProps={{
                  htmlInput: {
                    pattern: "[0-9]*",
                    "data-e2e": "bulk-grid-selection-row-count",
                  },
                }}
                aria-label={t("general.SELECT_ROWS_COUNT")}
              />
              <FramerTooltip
                content={rowCountApplyLabel}
                mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
              >
                <span
                  className="bulk-grid-selection__row-count-apply-wrap"
                  data-can-apply={canApplyRowCount ? "true" : "false"}
                >
                  <IconButton
                    size="small"
                    className="bulk-grid-selection__row-count-apply"
                    aria-label={rowCountApplyLabel}
                    disabled={!canApplyRowCount}
                    onMouseDown={onRowCountApplyMouseDown}
                    onClick={onRowCountApplyClick}
                    data-e2e="bulk-grid-selection-row-count-apply"
                  >
                    <Done fontSize="small" />
                  </IconButton>
                </span>
              </FramerTooltip>
            </BulkGridSelectionRowCountGroupStyled>
          )}

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
          >
            <BulkGridSelectionModeMenuItem
              mode={BulkGridSelectionModeEnum.All}
              label={t("general.SELECT_ALL_RESULTS")}
              selected={activeMode === BulkGridSelectionModeEnum.All}
              onSelect={onMenuModeSelect}
              e2eData="bulk-grid-selection-option-all"
            />
            <BulkGridSelectionModeMenuItem
              mode={BulkGridSelectionModeEnum.Page}
              label={t("general.SELECT_THIS_PAGE")}
              selected={activeMode === BulkGridSelectionModeEnum.Page}
              onSelect={onMenuModeSelect}
              e2eData="bulk-grid-selection-option-page"
            />
            {enableSelectRows && (
              <BulkGridSelectionModeMenuItem
                mode={BulkGridSelectionModeEnum.Rows}
                label={t("general.SELECT_ROWS")}
                selected={activeMode === BulkGridSelectionModeEnum.Rows}
                onSelect={onMenuModeSelect}
                e2eData="bulk-grid-selection-option-rows"
              />
            )}
          </Menu>
        </>
      ) : (
        <RidesSelectAllMobileIcon
          ariaLabel={selectAllLabel}
          onClick={onCheckboxClick}
        />
      )}
    </BulkGridSelectionWrapperStyled>
  );
};
